import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, RefreshCw, Sparkles } from "lucide-react";
import "../styles/technews.css";

const topics = [
  { label: "AI and Ethics", keywords: ["AI", "ethics", "governance"] },
  { label: "Tesla and Energy", keywords: ["Tesla Inc", "energy", "EV"] },
  { label: "Data Infrastructure", keywords: ["data centers"] },
  { label: "AI Research", keywords: ["AI research", "machine learning", "deep learning"] },
  { label: "Tech Policy", keywords: ["tech policy", "regulation", "legislation"] },
  {label : "Public Transit", keywords: ["public transit", "mass transit", "urban mobility"]},
];

const pageSize = 9;

const TechNews = () => {
  const [newsData, setNewsData] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedTopic, setSelectedTopic] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const activeTopic = topics[selectedTopic] || topics[0];

  useEffect(() => {
    setNewsData([]);
    setPage(1);
    setHasMore(true);
  }, [selectedTopic]);

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedTopic]);

  const fetchNews = async () => {
    if (!hasMore && page > 1) return;
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        process.env.REACT_APP_NEWS_API,
        {
          action: "getArticles",
          keyword: activeTopic.keywords,
          articlesPage: page,
          articlesCount: pageSize,
          articlesSortBy: "date",
          articlesSortByAsc: false,
          articlesArticleBodyLen: -1,
          resultType: "articles",
          forceMaxDataTimeWindow: 31,
          apiKey: process.env.REACT_APP_NEWS_API_CODE,
          lang: "eng",
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const results = response.data?.articles?.results || [];
      setNewsData((prev) => (page === 1 ? results : [...prev, ...results]));
      if (results.length < pageSize) {
        setHasMore(false);
      }
    } catch (err) {
      setError("Failed to load news articles. Please check your API settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setNewsData([]);
    setPage(1);
    setHasMore(true);
    fetchNews();
  };

  const cards = useMemo(() => newsData || [], [newsData]);

  return (
    <div className="technews-page">
      <section className="container py-5">
        <div className="technews-hero">
          <div>
            <p className="eyebrow">Tech news</p>
            <h1 className="display-6 fw-bold">Signals across AI, mobility, and data</h1>
            <p className="lead">
              Curated articles from trusted sources on the topics I track most for operations, AI governance,
              and infrastructure.
            </p>
            <div className="hero-actions">
              <button className="btn btn-primary btn-sm" onClick={handleRefresh} disabled={loading}>
                <RefreshCw size={16} className="me-1" />
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
          <div className="hero-meta">
            <div className="metric-card">
              <div className="metric-value">{cards.length}</div>
              <div className="metric-label">Articles loaded</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{activeTopic.label}</div>
              <div className="metric-label">Topic</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">Last 31 days</div>
              <div className="metric-label">Time window</div>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-4">
        <div className="topic-row">
          {topics.map((t, idx) => {
            const active = idx === selectedTopic;
            return (
              <button
                key={t.label}
                className={`chip ${active ? "chip--active" : ""}`}
                onClick={() => setSelectedTopic(idx)}
                aria-pressed={active}
              >
                <Sparkles size={14} className="me-1" />
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="container pb-5">
        {error ? (
          <div className="alert alert-warning" role="alert">
            {error}
          </div>
        ) : null}

        <div className="row g-4">
          {loading && cards.length === 0
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="col-md-4">
                  <div className="news-card skeleton" />
                </div>
              ))
            : cards.map((article, index) => (
                <div key={`${article.id || index}`} className="col-md-4">
                  <div className="news-card h-100">
                    {article.image ? (
                      <div
                        className="news-thumb"
                        style={{ backgroundImage: `url(${article.image})` }}
                        role="img"
                        aria-label={article.title}
                      />
                    ) : (
                      <div className="news-thumb placeholder">
                        <Sparkles size={18} />
                      </div>
                    )}
                    <div className="news-body">
                      <div className="small text-muted mb-1">
                        {article.source?.title || "Unknown source"} -{" "}
                        {article.date ? new Date(article.date).toLocaleDateString() : "Unknown date"}
                      </div>
                      <h5 className="mb-2">{article.title}</h5>
                      <p className="text-muted small flex-grow-1">
                        {article.body ? `${article.body.substring(0, 180)}...` : "No description available."}
                      </p>
                      <button
                        className="btn btn-outline-primary btn-sm mt-auto"
                        onClick={() =>
                          window.open(article.url, "_blank", "noopener,noreferrer")
                        }
                      >
                        Read more <ArrowRight size={14} className="ms-1" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        <div className="text-center mt-4">
          <button
            className="btn btn-secondary"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={loading || !hasMore}
          >
            {loading ? "Loading..." : hasMore ? "Load more" : "No more articles"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default TechNews;
